/**
 * Delivery Calendar Modal Component
 * Custom element for selecting delivery dates on product pages
 * Version: 2.0 - Adapted for Impulse theme (event-driven architecture)
 */

if (!customElements.get('delivery-calendar-modal')) {
  customElements.define(
    'delivery-calendar-modal',
    class DeliveryCalendarModal extends HTMLElement {
      constructor() {
        super();

        // State
        this.calendarData = null;
        this.selectedDate = null;
        this.currentMonthIndex = 0;
        this.zipcode = '';
        this.isNavigating = false;

        // DOM References
        this.overlay = this.querySelector('.delivery-calendar-modal__overlay');
        this.closeButton = this.querySelector('.delivery-calendar-modal__close');
        this.loader = this.querySelector('.delivery-calendar-modal__loader');
        this.errorContainer = this.querySelector('.delivery-calendar-modal__error');
        this.errorMessage = this.querySelector('.delivery-calendar-error-message');
        this.retryButton = this.querySelector('.delivery-calendar-retry');
        this.content = this.querySelector('.delivery-calendar-modal__content');
        this.zipcodeDisplay = this.querySelector('.delivery-calendar-zipcode-display');
        this.monthYearDisplay = this.querySelector('.calendar-month-year');
        this.prevButton = this.querySelector('.calendar-nav-prev');
        this.nextButton = this.querySelector('.calendar-nav-next');
        this.calendarDays = this.querySelector('.calendar-days');
        this.selectionContainer = this.querySelector('.delivery-calendar-selection');
        this.selectedDateDisplay = this.querySelector('.selected-date');
        this.selectedFeeNameDisplay = this.querySelector('.selected-fee-name');
        this.confirmButton = this.querySelector('.delivery-calendar-confirm');
        this.legendContainer = this.querySelector('.delivery-calendar-legend');
      }

      connectedCallback() {
        // Prevent duplicate event binding when modal is moved to body
        if (this.dataset.eventsbound === 'true') return;
        this.dataset.eventsbound = 'true';
        this.bindEvents();
      }

      bindEvents() {
        // Close modal events
        this.closeButton?.addEventListener('click', () => this.hide());
        this.overlay?.addEventListener('click', () => this.hide());

        // Escape key to close
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.hasAttribute('open')) {
            this.hide();
          }
        });

        // Month navigation
        this.prevButton?.addEventListener('click', () => this.navigateMonth(-1));
        this.nextButton?.addEventListener('click', () => this.navigateMonth(1));

        // Retry button
        this.retryButton?.addEventListener('click', () => this.fetchCalendar());

        // Confirm button
        this.confirmButton?.addEventListener('click', () => this.confirmSelection());

        // Calendar day clicks (delegated)
        this.calendarDays?.addEventListener('click', (e) => {
          const dayEl = e.target.closest('.calendar-day.available');
          if (dayEl) {
            this.selectDate(dayEl);
          }
        });
      }

      /**
       * Show the modal and start loading calendar data
       * @param {string} zipcode - 5-digit zip code
       */
      show(zipcode) {
        this.zipcode = zipcode;
        this.selectedDate = null;
        this.currentMonthIndex = 0;

        // Move modal to body to avoid parent container issues
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }

        // Reset UI state
        this.showLoader();
        this.updateSelectionDisplay();

        // Show modal
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');

        // Fetch calendar data
        this.fetchCalendar();
      }

      /**
       * Hide the modal
       */
      hide() {
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }

      /**
       * Fetch calendar data from API
       */
      async fetchCalendar() {
        this.showLoader();

        const sku = this.dataset.productSku;
        const url = `https://app.andersongiftbaskets.com/api/public_website/delivery/calendar?sku=${encodeURIComponent(sku)}&zipcode=${encodeURIComponent(this.zipcode)}`;

        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'accept': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (!data.success) {
            throw new Error(data.message || 'Unable to load delivery calendar');
          }

          this.calendarData = data.data;
          this.updateLegend();
          this.showContent();
          this.renderCalendar();

        } catch (error) {
          console.error('Delivery calendar error:', error);
          this.showError(error.message || 'Unable to load delivery dates. Please try again.');
        }
      }

      /**
       * Update the pricing legend based on API response
       */
      updateLegend() {
        if (!this.calendarData?.pricing || !this.legendContainer) return;

        const pricing = this.calendarData.pricing;
        let legendHTML = '';

        if (pricing.standard) {
          legendHTML += `<span class="legend-standard">Standard</span>`;
        }
        if (pricing.express) {
          legendHTML += `<span class="legend-express">Rush</span>`;
        }
        if (pricing.saturday) {
          legendHTML += `<span class="legend-saturday">Saturday</span>`;
        }

        this.legendContainer.innerHTML = legendHTML;
      }

      /**
       * Render the calendar for the current month
       */
      renderCalendar() {
        if (!this.calendarData?.calendar || this.calendarData.calendar.length === 0) {
          this.showError('No delivery dates available for this location.');
          return;
        }

        const monthData = this.calendarData.calendar[this.currentMonthIndex];
        if (!monthData) return;

        // Update month/year display
        this.monthYearDisplay.textContent = `${monthData.month_name} ${monthData.year}`;
        this.zipcodeDisplay.textContent = this.zipcode;

        // Update navigation buttons
        this.prevButton.disabled = this.currentMonthIndex === 0;
        this.nextButton.disabled = this.currentMonthIndex >= this.calendarData.calendar.length - 1;

        // Build calendar grid
        this.calendarDays.innerHTML = this.buildCalendarGrid(monthData);
      }

      /**
       * Build the calendar grid HTML for a month
       * @param {Object} monthData - Month data from API
       * @returns {string} HTML string
       */
      buildCalendarGrid(monthData) {
        let html = '';

        // Find day 1 in the array to get correct starting position
        // Use JavaScript Date to calculate the correct day of week
        const year = monthData.year;
        const month = monthData.month - 1; // JavaScript months are 0-indexed
        const firstDayDate = new Date(year, month, 1);
        const startDayOfWeek = firstDayDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

        // Debug: Show day names
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        console.log(`Calendar: ${monthData.month_name} ${year}`);
        console.log(`First day (${year}-${month + 1}-01) is ${dayNames[startDayOfWeek]} (index: ${startDayOfWeek})`);
        console.log(`Adding ${startDayOfWeek} empty cells before day 1`);

        // Add empty cells for days before the first of the month
        for (let i = 0; i < startDayOfWeek; i++) {
          html += '<div class="calendar-day empty" data-empty-index="' + i + '"><span class="calendar-day-number">&nbsp;</span></div>';
        }

        // Get today's date for highlighting (use local date components to match API format)
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        // Add day cells
        for (const day of monthData.days) {
          const classes = ['calendar-day'];
          let feeDisplay = '';

          // Check if this is today
          if (day.date === todayStr) {
            classes.push('today');
          }

          if (day.available) {
            classes.push('available');
            classes.push(day.delivery_type);
            feeDisplay = `<span class="calendar-day-fee">$${day.fee}</span>`;

            // Check if this day is selected
            if (this.selectedDate && this.selectedDate.date === day.date) {
              classes.push('selected');
            }
          } else {
            classes.push('unavailable');
          }

          // Use base64 encoding for day data to safely handle special characters
          const dayDataEncoded = btoa(JSON.stringify(day));
          html += `
            <div class="${classes.join(' ')}" data-date="${day.date}" data-day-encoded="${dayDataEncoded}">
              <span class="calendar-day-number">${day.day}</span>
              ${feeDisplay}
            </div>
          `;
        }

        return html;
      }

      /**
       * Navigate to previous/next month
       * @param {number} direction - -1 for previous, 1 for next
       */
      navigateMonth(direction) {
        // Debounce rapid clicks
        if (this.isNavigating) return;

        const newIndex = this.currentMonthIndex + direction;

        if (newIndex >= 0 && newIndex < this.calendarData.calendar.length) {
          this.isNavigating = true;
          this.currentMonthIndex = newIndex;
          this.renderCalendar();

          // Allow navigation again after a short delay
          setTimeout(() => {
            this.isNavigating = false;
          }, 150);
        }
      }

      /**
       * Handle date selection
       * @param {HTMLElement} dayEl - Clicked day element
       */
      selectDate(dayEl) {
        // Check if clicking on already selected date (confirm action)
        if (dayEl.classList.contains('selected') && dayEl.classList.contains('ready-to-confirm')) {
          this.confirmSelection();
          return;
        }

        // Remove previous selection
        const prevSelected = this.calendarDays.querySelector('.calendar-day.selected');
        if (prevSelected) {
          prevSelected.classList.remove('selected');
          prevSelected.classList.remove('ready-to-confirm');
        }

        // Add selection to clicked day
        dayEl.classList.add('selected');

        // After a short delay, show "confirm" state
        setTimeout(() => {
          if (dayEl.classList.contains('selected')) {
            dayEl.classList.add('ready-to-confirm');
          }
        }, 150);

        // Store selected date data (decode from base64)
        try {
          this.selectedDate = JSON.parse(atob(dayEl.dataset.dayEncoded));
          this.updateSelectionDisplay();
        } catch (e) {
          console.error('Error parsing day data:', e);
        }
      }

      /**
       * Parse a date string (YYYY-MM-DD) into a Date object at noon local time
       * This avoids timezone issues by parsing components directly
       * @param {string} dateStr - Date in YYYY-MM-DD format
       * @returns {Date}
       */
      parseDateString(dateStr) {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day, 12, 0, 0);
      }

      /**
       * Update the selection summary display
       */
      updateSelectionDisplay() {
        if (this.selectedDate) {
          // Format date nicely - parse date components to avoid timezone issues
          const dateObj = this.parseDateString(this.selectedDate.date);
          const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
          const formattedDate = dateObj.toLocaleDateString('en-US', options);

          this.selectedDateDisplay.textContent = formattedDate;

          // Format the fee name display
          let feeName = this.selectedDate.fee_name;
          // Rename Express to Rush
          if (feeName.toLowerCase().includes('express')) {
            feeName = feeName.replace(/express/i, 'Rush');
          }

          let feeText;
          if (this.selectedDate.delivery_type === 'standard') {
            feeText = `${feeName} ($${this.selectedDate.fee} or free after $99)`;
          } else {
            feeText = `${feeName} ($${this.selectedDate.fee})`;
          }

          this.selectedFeeNameDisplay.textContent = feeText;
          this.selectionContainer.hidden = false;
          this.confirmButton.disabled = false;
        } else {
          this.selectionContainer.hidden = true;
          this.confirmButton.disabled = true;
        }
      }

      /**
       * Confirm selection and add to cart
       */
      async confirmSelection() {
        if (!this.selectedDate) return;

        // Show loading state on button
        this.confirmButton.disabled = true;
        this.confirmButton.textContent = 'Adding to cart...';

        try {
          // Get the product form
          const sectionId = this.dataset.sectionId;

          // Try multiple ways to find the form
          let form = document.querySelector(`#AddToCartForm-${sectionId}`) ||
                     document.querySelector(`form[id*="${sectionId}"]`) ||
                     document.querySelector('.product-single__form') ||
                     document.querySelector('form[action*="/cart/add"]');

          // Also check for product-form custom element
          let productFormEl = form?.closest('product-form');

          if (!form) {
            // Try finding via zipcode wrapper
            const zipcodeWrapper = document.querySelector(`#delivery-zipcode-wrapper-${sectionId}`);
            if (zipcodeWrapper) {
              form = zipcodeWrapper.closest('form') ||
                     zipcodeWrapper.parentElement?.querySelector('form');
            }
          }

          if (!form) {
            throw new Error('Product form not found');
          }

          // Format date for display (use parseDateString to avoid timezone issues)
          const dateObj = this.parseDateString(this.selectedDate.date);
          const displayDate = dateObj.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });

          // Remove any existing delivery property inputs
          form.querySelectorAll('input[name^="properties[Requested Delivery"], input[name^="properties[Expected Delivery"], input[name^="properties[_requested_delivery"], input[name^="properties[_expected_delivery"]').forEach(el => el.remove());

          // Add hidden inputs for delivery properties
          const properties = [
            { name: 'properties[Requested Delivery Date]', value: displayDate },
            { name: 'properties[Expected Delivery Fee]', value: `$${this.selectedDate.fee}` },
            { name: 'properties[Expected Delivery Type]', value: this.selectedDate.fee_name },
            { name: 'properties[Expected Delivery Zip Code]', value: this.zipcode },
            { name: 'properties[_requested_delivery_date]', value: this.selectedDate.date },
            { name: 'properties[_expected_delivery_fee]', value: this.selectedDate.fee },
            { name: 'properties[_expected_delivery_type]', value: this.selectedDate.delivery_type },
            { name: 'properties[_expected_delivery_zip_code]', value: this.zipcode }
          ];

          properties.forEach(prop => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = prop.name;
            input.value = prop.value;
            form.appendChild(input);
          });

          // Close modal first
          this.hide();

          // Set flag to allow the submission to proceed
          // Set on both form and productFormEl if it exists
          form.dataset.deliveryDateSelected = 'true';
          if (productFormEl) {
            productFormEl.dataset.deliveryDateSelected = 'true';
          }

          // Trigger form submission
          form.requestSubmit();

        } catch (error) {
          console.error('Add to cart error:', error);
          alert(error.message || 'Failed to add to cart. Please try again.');
          this.confirmButton.textContent = 'Confirm & Add to Cart';
          this.confirmButton.disabled = false;
        }
      }

      /**
       * Show loading state
       */
      showLoader() {
        this.loader.hidden = false;
        this.errorContainer.hidden = true;
        this.content.hidden = true;
      }

      /**
       * Show error state
       * @param {string} message - Error message to display
       */
      showError(message) {
        this.loader.hidden = true;
        this.errorContainer.hidden = false;
        this.content.hidden = true;
        this.errorMessage.textContent = message;
      }

      /**
       * Show calendar content
       */
      showContent() {
        this.loader.hidden = true;
        this.errorContainer.hidden = true;
        this.content.hidden = false;
      }
    }
  );
}

/**
 * Delivery Zipcode Input Handler
 * Manages zip code validation and triggers calendar modal
 */
class DeliveryZipcodeHandler {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.input = wrapper.querySelector('.delivery-zipcode-input');
    this.errorEl = wrapper.querySelector('.delivery-zipcode-error');

    if (this.input) {
      this.bindEvents();
    }
  }

  bindEvents() {
    // Only allow numbers
    this.input.addEventListener('input', (e) => {
      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5);
      this.clearError();
    });

    // Clear error on focus
    this.input.addEventListener('focus', () => {
      this.clearError();
    });
  }

  validate() {
    const value = this.input.value.trim();
    const isValid = /^\d{5}$/.test(value);

    if (!isValid) {
      this.showError();
      return false;
    }

    this.clearError();
    return true;
  }

  getValue() {
    return this.input.value.trim();
  }

  showError() {
    this.wrapper.classList.add('has-error');
    this.input.focus();
  }

  clearError() {
    this.wrapper.classList.remove('has-error');
  }
}

/**
 * Initialize delivery calendar integration with product form
 * Works with both event-driven <product-form> and plain forms
 */
function initDeliveryCalendar() {
  // Find all delivery calendar wrappers on the page
  document.querySelectorAll('[id^="delivery-zipcode-wrapper"]').forEach((zipcodeWrapper) => {
    // Extract section ID from wrapper ID
    const wrapperId = zipcodeWrapper.id;
    const sectionId = wrapperId.replace('delivery-zipcode-wrapper-', '');

    // Find the corresponding calendar modal
    const calendarModal = document.querySelector(`#DeliveryCalendar-${sectionId}`) ||
                          document.querySelector('[id^="DeliveryCalendar"]');

    if (!calendarModal) {
      console.warn('Delivery calendar modal not found for section:', sectionId);
      return;
    }

    // Find the form - could be wrapped in <product-form> or just a plain form
    const form = zipcodeWrapper.closest('form') ||
                 zipcodeWrapper.parentElement?.querySelector('form') ||
                 document.querySelector(`#AddToCartForm-${sectionId}`) ||
                 document.querySelector('form[action*="/cart/add"]') ||
                 document.querySelector('.product-single__form');

    if (!form) {
      console.warn('Product form not found for delivery calendar');
      return;
    }

    // Skip if already initialized
    if (form.dataset.deliveryCalendarInit === 'true') return;
    form.dataset.deliveryCalendarInit = 'true';

    const zipcodeHandler = new DeliveryZipcodeHandler(zipcodeWrapper);

    // Check if form is inside a <product-form> custom element (event-driven)
    const productFormEl = form.closest('product-form');

    if (productFormEl) {
      // Use event-driven approach for <product-form> custom element
      console.log('Delivery calendar: Using event-driven mode');

      productFormEl.addEventListener('product-form:cart:request', (event) => {
        // Check if delivery date has already been selected
        if (productFormEl.dataset.deliveryDateSelected === 'true') {
          productFormEl.dataset.deliveryDateSelected = 'false';
          return; // Let the request proceed
        }

        // Validate zipcode first
        if (!zipcodeHandler.validate()) {
          event.preventDefault();
          productFormEl.enableSubmitButton?.();
          return;
        }

        // Prevent the default cart request - show calendar first
        event.preventDefault();
        productFormEl.enableSubmitButton?.();
        calendarModal.show(zipcodeHandler.getValue());
      });
    } else {
      // Use form submit interception for plain forms
      console.log('Delivery calendar: Using form interception mode');

      form.addEventListener('submit', (evt) => {
        // Check if delivery date has already been selected
        if (form.dataset.deliveryDateSelected === 'true') {
          form.dataset.deliveryDateSelected = 'false';
          return; // Let it proceed
        }

        // Validate zipcode
        if (!zipcodeHandler.validate()) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          return;
        }

        // Stop the submission and show calendar
        evt.preventDefault();
        evt.stopImmediatePropagation();
        calendarModal.show(zipcodeHandler.getValue());
      }, true); // Use capturing phase
    }
  });
}

// Initialize after DOM is ready and after theme JS has initialized
function safeInit() {
  // Wait for any custom elements to be defined
  setTimeout(() => {
    initDeliveryCalendar();
  }, 200);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeInit);
} else {
  safeInit();
}
